import { forwardRef, useRef, useEffect, useMemo, useCallback } from 'react';
import './CarTypePopup.scss';
import DraggablePopup from './Popup/DraggablePopup';
import { DraggablePopupRef, DraggablePopupState } from './Popup/DraggablePopup';
import ReactLoading from 'react-loading';

import { useEstimatedFare } from 'context/EstimatedFareProvider';
import { useFetchDiscount } from 'hooks/useFetchDiscount';
import { EstimatedFareItem } from 'api/taxiTrip/model/FEModel';

/**
 * 滾動到指定元素
 * @param element 要滾動到的元素
 * @param container 滾動容器元素
 * @param offset 額外偏移量
 */
const scrollToElement = (element: HTMLElement, container: HTMLElement, offset = 0) => {
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  // 計算元素相對於容器的頂部距離
  const scrollTop = elementRect.top - containerRect.top + container.scrollTop + offset;
  container.scrollTo({
    top: scrollTop,
    behavior: 'smooth',
  });
};

export interface DiscountedCarTypeFare extends EstimatedFareItem {
  discountedFare: {
    originalFare: number;
    discountedOrderAmount: number;
    discountAmount: number;
    hasRange: boolean;
    rangeOriginalAmount: { min: number; max: number };
    rangeDiscountAmount: { min: number; max: number };
  };
}

const CarTypeItem = ({
  carType,
  scrollContainer,
  onSelectCarType,
}: {
  carType: DiscountedCarTypeFare;
  scrollContainer: React.RefObject<HTMLDivElement> | null;
  onSelectCarType: (carType: DiscountedCarTypeFare) => void;
}) => {
  const carTypeRefs = useRef<HTMLDivElement>(null);
  const { estimatedFare } = useEstimatedFare();

  const formatNumberWithCommas = useCallback((number: number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }, []);

  const getAmount = useCallback(
    (type: 'original' | 'discounted', carType: DiscountedCarTypeFare) => {
      if (type === 'original') {
        if (carType.discountedFare.hasRange) {
          return `${formatNumberWithCommas(
            carType.discountedFare.rangeOriginalAmount.min
          )} ~ ${formatNumberWithCommas(carType.discountedFare.rangeOriginalAmount.max)}`;
        }
        return formatNumberWithCommas(carType.discountedFare.originalFare);
      }
      if (carType.discountedFare.hasRange) {
        return `${formatNumberWithCommas(
          carType.discountedFare.rangeDiscountAmount.min
        )} ~ ${formatNumberWithCommas(carType.discountedFare.rangeDiscountAmount.max)}`;
      }
      return formatNumberWithCommas(carType.discountedFare.discountedOrderAmount);
    },
    [formatNumberWithCommas]
  );

  useEffect(() => {
    if (
      carTypeRefs.current &&
      scrollContainer?.current &&
      estimatedFare.productId === carType.productId &&
      estimatedFare.fareId === carType.fareId
    ) {
      scrollToElement(carTypeRefs.current, scrollContainer.current, -4);
    }
  }, [carTypeRefs, scrollContainer, estimatedFare, carType.productId, carType.fareId]);

  return (
    <div
      ref={carTypeRefs}
      className={`car-type-item__container ${
        carType.productId === estimatedFare.productId ? 'selected' : ''
      }`}
      onClick={() => onSelectCarType(carType)}
    >
      {carType.pictureUrl && (
        <div className="car-type-item__image">
          <img src={carType.pictureUrl} alt={carType.displayName} />
        </div>
      )}
      <div className="car-type-item__body">
        <div className="car-type-item__content">
          <div className="car-type-item__title">
            <p className="car-type-item__name">
              <span>{carType.displayName}</span>
              <span className="car-type-item__capacity">
                <i className="icon-people" aria-hidden="true" />
                <span>{carType.capacity}</span>
              </span>
            </p>
            {typeof carType.pickupEstimate === 'number' && (
              <div className="car-type-item__pickupEstimate">
                預計 {Math.ceil(carType.pickupEstimate / 60)} 分鐘後抵達
              </div>
            )}
          </div>
          <div className="car-type-item__price">
            <p>${getAmount('discounted', carType)}</p>
            {carType.discountedFare.discountedOrderAmount !==
              carType.discountedFare.originalFare && (
              <del className="original-price">${getAmount('original', carType)}</del>
            )}
          </div>
        </div>
        <div className="car-type-item__description">{carType.description}</div>
      </div>
    </div>
  );
};

interface DraggablePopupProps {
  isOpen: boolean;
  onClose: () => void;
  discountedCarTypeFares: DiscountedCarTypeFare[];
  initPosition?: 'open' | 'half' | 'peek';
  popupBottom?: number;
  height?: string;
  title?: string;
  isLoading?: boolean;
  customStyle?: React.CSSProperties;
}

/**
 * 車種選擇彈窗，只包裝 DraggablePopup，無多餘邏輯
 * @param {object} props
 * @param {boolean} props.isOpen - 是否開啟彈窗
 * @param {function} props.onClose - 關閉 callback
 * @param {DiscountedCarTypeFare[]} props.discountedCarTypeFares - 折扣後的車種列表
 * @param {'open' | 'half' | 'peek'} [props.initPosition] - 初始位置
 * @param {string} [props.popupBottom] - 底部間隔 (position: fixed 的 bottom)
 * @param {string} [props.height] - 彈窗高度
 * @param {string} [props.title] - 標題
 * @param {boolean} [props.isLoading] - 是否正在載入
 * @param {React.CSSProperties} [props.customStyle] - 自訂樣式
 */
const CarTypePopup = forwardRef<DraggablePopupRef, DraggablePopupProps>((props, ref) => {
  const scrollContainer = useRef<HTMLDivElement>(null);
  const draggablePopupRef = useRef<DraggablePopupRef>(null);
  const { estimatedFare, setEstimatedFare } = useEstimatedFare();
  const { updateDiscountUsage } = useFetchDiscount();

  // 處理選擇車型的點擊事件
  const handleSelectCarType = (carType: DiscountedCarTypeFare) => {
    setEstimatedFare({ ...carType });
    updateDiscountUsage({
      updatedOrderAmount: carType.fare,
      fareId: carType.fareId,
    });
  };

  // 使用 useMemo 緩存選中的車型，避免不必要的重新計算
  const selectedCarType = useMemo(() => {
    return props.discountedCarTypeFares.find(
      (carType: DiscountedCarTypeFare) => carType.productId === estimatedFare.productId
    );
  }, [props.discountedCarTypeFares, estimatedFare.productId]);

  // 監聽彈窗位置變化的處理函數
  const handlePositionChange = (pos: DraggablePopupState) => {
    // 如果位置變更為 peek，自動滾動到選中的車型位置
    if (pos === 'peek' && scrollContainer.current && estimatedFare && selectedCarType) {
      const selectedElement = scrollContainer.current.querySelector(
        `.car-type-item__container.selected`
      ) as HTMLElement;
      if (selectedElement) {
        setTimeout(() => {
          if (scrollContainer.current) {
            scrollToElement(selectedElement, scrollContainer.current, -4);
          }
        }, 300);
      }
    }
  };

  // 將原始 ref 與自定義 ref 合併
  useEffect(() => {
    if (ref) {
      if (typeof ref === 'function') {
        ref(draggablePopupRef.current);
      } else {
        ref.current = draggablePopupRef.current;
      }
    }
  }, [ref]);

  return (
    <DraggablePopup
      {...props}
      ref={draggablePopupRef}
      peekHeight={164}
      onPositionChange={handlePositionChange}
    >
      <div
        ref={scrollContainer}
        className="car-type-list"
        style={{ height: '100%', overflowY: 'auto' }}
      >
        {props.isLoading ? (
          <ReactLoading
            className="car-type-popup__loading"
            type="bars"
            color="#00CCC0"
            height={60}
            width={60}
          />
        ) : props.discountedCarTypeFares.length > 0 ? (
          props.discountedCarTypeFares.map((carType: DiscountedCarTypeFare) => (
            <CarTypeItem
              key={carType.productId}
              carType={carType}
              scrollContainer={scrollContainer}
              onSelectCarType={handleSelectCarType}
            />
          ))
        ) : (
          <p className="car-type-popup__no-car">暫無車輛可供搭乘</p>
        )}
      </div>
    </DraggablePopup>
  );
});

CarTypePopup.displayName = 'CarTypePopup';
export default CarTypePopup;
